import React from "react";
import { useNavigate } from "react-router-dom";
import {
  Feather,
  Inbox,
  Sparkles,
  Shirt,
  KanbanSquare,
  BarChart3,
  MessageCircle,
  Megaphone,
  FileText,
  ArrowRight,
  Bot,
} from "lucide-react";

const FEATURES = [
  {
    icon: Inbox,
    title: "Unified inquiry inbox",
    body: "Every WhatsApp, Instagram DM, comment, phone call, and walk-in — logged in one place and auto-categorized by Claude Sonnet 4.5.",
  },
  {
    icon: Sparkles,
    title: "Saheli Match — visual search",
    body: "Customer sends a photo or Instagram link? Drop it in, and Saheli (via Gemini) finds the closest sarees in your catalog by fabric, motif, and colour.",
  },
  {
    icon: Bot,
    title: "AI-drafted replies",
    body: "One click drafts a warm reply in English, Hindi, or Hinglish — with the matched saree's details and a craft/heritage note, editable before sending.",
  },
  {
    icon: KanbanSquare,
    title: "Order pipeline",
    body: "Drag inquiries across six stages — New → Quoted → Confirmed → Payment → Dispatched → Delivered — with live analytics that refresh every 15 seconds.",
  },
  {
    icon: Shirt,
    title: "Weaver timeline",
    body: "Made-to-order sarees get a live 45-day progress bar — yarn dyeing, loom setup, weaving, finishing — so no customer ever wonders again.",
  },
  {
    icon: Megaphone,
    title: "One-click WhatsApp broadcast",
    body: "New arrival? Push its card to hand-picked past buyers over ManyChat in seconds — with a caption you control and a saree image attached.",
  },
  {
    icon: FileText,
    title: "GST-ready invoices",
    body: "Every confirmed order generates a PDF invoice with CGST + SGST breakdown, Juspay payment reference, and the Silk Mart Sarees monogram.",
  },
  {
    icon: BarChart3,
    title: "Reports & SLA nudges",
    body: "See conversion by inquiry type, average response time, follow-up completion, and get a bottom-right nudge for any WhatsApp reply overdue by 15 minutes.",
  },
];

const CHANNELS = [
  { name: "WhatsApp", accent: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  { name: "Instagram DM", accent: "bg-fuchsia-50 text-fuchsia-700 border-fuchsia-200" },
  { name: "Instagram Comment", accent: "bg-pink-50 text-pink-700 border-pink-200" },
  { name: "Phone Call", accent: "bg-sky-50 text-sky-700 border-sky-200" },
  { name: "Walk-in", accent: "bg-amber-50 text-amber-700 border-amber-200" },
];

export default function Landing() {
  const navigate = useNavigate();
  const enter = () => navigate("/dashboard");

  return (
    <div className="min-h-screen">
      {/* Nav */}
      <header className="max-w-6xl mx-auto px-6 lg:px-10 py-6 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-xl bg-terracotta text-white grid place-items-center shadow-sm">
            <Feather className="w-4 h-4" strokeWidth={2.25} />
          </div>
          <div>
            <div
              className="font-serif text-2xl leading-none text-stone-900"
              data-testid="landing-brand"
            >
              Saheli
            </div>
            <div className="text-[11px] uppercase tracking-[0.22em] text-stone-400 mt-0.5">
              Silk Mart Sarees
            </div>
          </div>
        </div>
        <button
          data-testid="landing-cta-nav"
          onClick={enter}
          className="text-sm text-stone-700 hover:text-terracotta transition-colors flex items-center gap-1.5"
        >
          Open dashboard
          <ArrowRight className="w-3.5 h-3.5" />
        </button>
      </header>

      {/* Hero */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 pt-8 lg:pt-16 grid lg:grid-cols-[1.15fr_1fr] gap-10 lg:gap-16 items-center">
        <div className="animate-fade-up">
          <div className="overline">A staff dashboard for handloom D2C</div>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.05] text-stone-900 mt-3">
            Every WhatsApp,
            <br />
            every Instagram,
            <br />
            <span className="text-terracotta">every saree found.</span>
          </h1>
          <p className="text-stone-600 text-base sm:text-lg leading-relaxed mt-5 max-w-xl">
            Saheli is the operations backbone for{" "}
            <b className="text-stone-900">Silk Mart Sarees</b> — a D2C handloom
            brand rooted in Banarasi & Kanjivaram heritage. Every customer
            message is auto-logged, matched to a saree in the catalog, and
            answered with an AI-drafted reply — all before the loom sees its
            next weft.
          </p>
          <div className="mt-8 flex items-center gap-3 flex-wrap">
            <button
              data-testid="landing-cta-primary"
              onClick={enter}
              className="bg-terracotta hover:bg-terracotta-dark text-white rounded-full px-6 py-3 text-sm font-medium flex items-center gap-2 shadow-sm active:scale-[0.98] transition-all"
            >
              <Sparkles className="w-4 h-4" />
              Enter the dashboard
              <ArrowRight className="w-3.5 h-3.5" />
            </button>
            <a
              data-testid="landing-cta-secondary"
              href="#tour"
              className="text-sm text-stone-600 hover:text-terracotta flex items-center gap-1.5 px-3 py-3"
            >
              See what's inside
              <ArrowRight className="w-3.5 h-3.5" />
            </a>
          </div>

          <div className="mt-8 flex items-center gap-2 flex-wrap">
            {CHANNELS.map((c) => (
              <span key={c.name} className={`heritage-badge ${c.accent}`}>
                {c.name}
              </span>
            ))}
          </div>
        </div>

        <div className="relative">
          <div className="absolute -top-10 -right-6 w-40 h-40 rounded-full bg-terracotta/10 blur-3xl" />
          <div className="absolute -bottom-8 -left-6 w-32 h-32 rounded-full bg-saffron/15 blur-3xl" />
          <img
            src="https://images.unsplash.com/photo-1610189013429-a703f4b245cf?crop=entropy&cs=srgb&fm=jpg&w=900&q=85"
            alt="Handloom saree"
            className="relative rounded-3xl border border-stone-200 shadow-md w-full aspect-[4/5] object-cover"
          />
          <div className="paper-card absolute -bottom-6 -left-6 p-3 max-w-[240px] shadow-md hidden sm:block">
            <div className="flex items-center gap-2 mb-1.5">
              <div className="w-6 h-6 rounded-md bg-terracotta/10 text-terracotta grid place-items-center">
                <Sparkles className="w-3 h-3" />
              </div>
              <div className="text-[11px] uppercase tracking-widest text-stone-400 font-bold">
                Saheli Match
              </div>
            </div>
            <div className="text-xs text-stone-700 leading-snug">
              <b>Katan Bridal Red Banarasi</b>
              <br />
              <span className="text-terracotta font-medium">89% match</span> ·
              ₹24,500 · In stock
            </div>
          </div>
          <div className="paper-card absolute -top-6 -right-4 px-3 py-2 shadow-md hidden sm:flex items-center gap-2">
            <MessageCircle className="w-4 h-4 text-emerald-600" />
            <div className="text-[11px] text-stone-700">
              <span className="text-emerald-700 font-semibold">
                {" "}
                42 inquiries
              </span>{" "}
              logged today
            </div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section
        id="tour"
        className="max-w-6xl mx-auto px-6 lg:px-10 py-20 lg:py-28"
      >
        <div className="max-w-2xl">
          <div className="overline">How Saheli works</div>
          <h2 className="font-serif text-3xl sm:text-4xl text-stone-900 mt-2">
            A loom for your inbox
          </h2>
          <p className="text-stone-600 mt-3 leading-relaxed">
            Every message flows in through ManyChat. Claude classifies it,
            Gemini finds the saree, Saheli drafts the reply. You edit,
            approve, and send — all inside one warm, editorial workspace.
          </p>
        </div>
        <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {FEATURES.map((f) => {
            const Icon = f.icon;
            return (
              <div
                key={f.title}
                data-testid={`landing-feature-${f.title
                  .toLowerCase()
                  .replace(/[^a-z0-9]+/g, "-")}`}
                className="paper-card p-5 hover:-translate-y-1 hover:border-stone-300 transition-all duration-300"
              >
                <div className="w-9 h-9 rounded-xl bg-terracotta/10 text-terracotta grid place-items-center mb-3">
                  <Icon className="w-4 h-4" />
                </div>
                <div className="font-serif text-lg text-stone-900 leading-tight">
                  {f.title}
                </div>
                <p className="text-sm text-stone-600 leading-relaxed mt-2">
                  {f.body}
                </p>
              </div>
            );
          })}
        </div>
      </section>

      {/* Story strip */}
      <section className="bg-gradient-to-br from-terracotta/5 via-transparent to-saffron/5 border-y border-stone-200/60">
        <div className="max-w-6xl mx-auto px-6 lg:px-10 py-16 grid lg:grid-cols-[1fr_1.2fr] gap-10 items-center">
          <div>
            <div className="overline">Built for the craft</div>
            <h2 className="font-serif text-3xl sm:text-4xl text-stone-900 mt-2 leading-tight">
              Handloom is human.
              <br />
              Saheli keeps it that way.
            </h2>
          </div>
          <div className="space-y-4 text-stone-700 leading-relaxed">
            <p>
              Silk Mart Sarees works with weavers across Varanasi and Kanchipuram —
              every saree is a story of yarn dyeing, loom setup, weeks of
              throwing the shuttle by hand, and a final press. Customers ask
              hard questions: "Is this katan silk? Will you make it in emerald?
              When can you ship?"
            </p>
            <p>
              Saheli holds all that context so the humans on the shop floor can
              stay warm, personal, and fast. AI does the heavy lifting on
              triage and search; staff keep the conversation heart-first.
            </p>
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-20 lg:py-28 text-center">
        <div className="overline">Ready?</div>
        <h2 className="font-serif text-4xl sm:text-5xl text-stone-900 mt-3">
          Open the loom.
        </h2>
        <p className="text-stone-600 mt-4 max-w-xl mx-auto">
          Open the Saheli dashboard — the demo runs on a curated Silk Mart Sarees catalog
          with a live ManyChat webhook.
        </p>
        <button
          data-testid="landing-cta-footer"
          onClick={enter}
          className="mt-8 inline-flex items-center gap-2 bg-terracotta hover:bg-terracotta-dark text-white rounded-full px-6 py-3 text-sm font-medium shadow-sm active:scale-[0.98] transition-all"
        >
          <Sparkles className="w-4 h-4" />
          Enter the dashboard
          <ArrowRight className="w-3.5 h-3.5" />
        </button>
      </section>

      <footer className="border-t border-stone-200/60 py-6 text-center text-xs text-stone-400">
        Saheli · Silk Mart Sarees · Woven with care in India ·{" "}
        <button
          type="button"
          data-testid="landing-demo-entry"
          onClick={enter}
          className="hover:text-terracotta underline underline-offset-2 cursor-pointer"
        >
          Enter demo
        </button>
      </footer>
    </div>
  );
}
